import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../lib/api";
import useCanonical from "../lib/useCanonical";
import { Search } from "lucide-react";
import Newsletter from "../components/Newsletter";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// FAQ block at the bottom — captures question-intent searches ("what does
// astringency mean in tea?", "what is the difference between oxidation and
// fermentation?") and powers the FAQPage JSON-LD for featured-snippet eligibility.
const FAQ_ITEMS = [
  {
    q: "What is a tea glossary?",
    a: "A tea glossary is a reference of the specialist vocabulary tea drinkers, growers, and tasters use — covering botany (Camellia sinensis, var. assamica), processing terms (oxidation, withering, kill-green), preparation rituals (gongfu, gaiwan, kyusu), and sensory descriptors (astringency, umami, hou yun). The Tea List glossary defines 75 essential tea terms with linked guides to each topic.",
  },
  {
    q: "What does oxidation mean in tea?",
    a: "Oxidation is the enzymatic browning process that transforms freshly plucked green tea leaves into black or oolong tea. The crushed leaf is exposed to oxygen, which lets natural polyphenols react and develop deeper colour, body, and flavour. Fully oxidised leaf becomes black tea; partial oxidation produces oolong; green tea is heat-fixed to stop oxidation almost entirely.",
  },
  {
    q: "What is the difference between oxidation and fermentation in tea?",
    a: "Oxidation is a chemical reaction with oxygen catalysed by enzymes inside the tea leaf — used to make black and oolong teas. Fermentation involves microbial activity, where bacteria, yeasts, or moulds further transform the leaf — this is what creates aged pu-erh teas. Most tea labelled 'fermented' is actually oxidised; true fermentation is rare and largely unique to dark/aged teas.",
  },
  {
    q: "What does astringency mean in tea?",
    a: "Astringency is the dry, mouth-puckering sensation a tea leaves on your tongue and gums — caused by tannins binding briefly with the proteins in your saliva. Light astringency is desirable in many black teas and green teas; heavy astringency usually signals over-brewing, water that's too hot, or too much leaf for the volume of water.",
  },
  {
    q: "What does 'first flush' mean?",
    a: "First flush refers to the very first picking of a tea garden in spring — typically March in Darjeeling or early April in Japan. The shoots are young, fragrant, and high in amino acids, producing a light, complex cup. First-flush teas are the most prized and most expensive harvest of the year.",
  },
  {
    q: "What is gongfu tea?",
    a: "Gongfu (or gongfu cha) is the traditional Chinese style of brewing tea using a small teapot or gaiwan, a higher leaf-to-water ratio, and many short infusions. Each pour brings out a different facet of the leaf. The word 'gongfu' means 'skill from practice' — it is a meditative, attentive way of drinking tea, and the lineage from which most ceremonial tea culture descends.",
  },
  {
    q: "What is umami in tea?",
    a: "Umami is the savoury 'fifth taste' that gives high-end Japanese teas — especially gyokuro and matcha — their rich, broth-like depth. It comes from L-theanine, an amino acid that develops most strongly in shaded teas. Combined with caffeine, L-theanine produces the calm, focused state often associated with green tea drinking.",
  },
  {
    q: "Are tea bags the same as loose-leaf tea?",
    a: "No. Tea bags typically contain 'fannings' or 'dust' — the smallest, lowest-grade fragments left after processing whole leaves. They brew quickly and consistently but lack the depth and aroma of intact loose leaves. Many tea bags also use plastic mesh or chemically treated paper, releasing microplastics into the cup. See our guide on tea bags vs loose-leaf for a deeper look.",
  },
];

export default function Glossary() {
  useCanonical("/glossary");
  const [data, setData] = useState({ entries: [], categories: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    http
      .get("/glossary")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    // `http` is a stable module import; `setData`/`setLoading` are stable React refs.
  }, []);  

  // SEO + JSON-LD
  useEffect(() => {
    document.title = "Tea Glossary: 75 Essential Tea Terms Explained — The Tea List";
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [, key, val] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
        if (key && val) el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    const description =
      "A clear, plain-English tea glossary — 75 essential tea terms across botany, processing, brewing, and tasting. From astringency to gongfu, oxidation to umami, with linked guides to each topic.";
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="keywords"]', "content", "tea glossary, tea terms, tea terminology, tea tasting terms, tea brewing terms, types of tea, oxidation, gongfu, matcha glossary");
    setMeta('meta[property="og:title"]', "content", "Tea Glossary: 75 Essential Tea Terms Explained");
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:type"]', "content", "website");

    if (data.entries.length) {
      const origin = window.location.origin.replace("tea-time-site-1.preview.emergentagent.com", "thetealist.com");
      const ld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "DefinedTermSet",
            "@id": `${origin}/glossary#termset`,
            name: "The Tea List Glossary",
            description,
            url: `${origin}/glossary`,
            hasDefinedTerm: data.entries.map((e) => ({
              "@type": "DefinedTerm",
              "@id": `${origin}/glossary/${e.slug}`,
              name: e.term,
              description: e.short_def,
              inDefinedTermSet: `${origin}/glossary#termset`,
              termCode: e.slug,
            })),
          },
          {
            "@type": "FAQPage",
            mainEntity: FAQ_ITEMS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      };
      let el = document.getElementById("ld-glossary");
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = "ld-glossary";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(ld);
    }
    return () => {
      document.title = "The Tea List — Australia's independent tea journal";
      document.getElementById("ld-glossary")?.remove();
    };
    // setMeta + ld payload are derived from `data` inside the effect.
  }, [data]);  

  const filtered = useMemo(() => {
    let out = data.entries;
    if (activeCategory !== "All") out = out.filter((e) => e.category === activeCategory);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter(
        (e) => e.term.toLowerCase().includes(q) || e.short_def.toLowerCase().includes(q)
      );
    }
    return out;
  }, [data.entries, activeCategory, query]);

  const grouped = useMemo(() => {
    const map = {};
    for (const e of filtered) {
      const letter = e.term[0].toUpperCase();
      const key = ALPHABET.includes(letter) ? letter : "#";
      (map[key] = map[key] || []).push(e);
    }
    return map;
    // ALPHABET is a module constant; safe to omit.
  }, [filtered]);  

  const lettersWithEntries = ALPHABET.filter((l) => grouped[l]?.length);

  return (
    <div className="relative z-10" data-testid="glossary-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-12">
        <p className="overline mb-4">The Tea List · Glossary</p>
        <h1 className="headline-xl text-5xl md:text-7xl">
          Every word a tea-drinker
          <br />
          <span className="italic text-[#1e4a35]">eventually</span> needs.
        </h1>
        <p className="mt-8 max-w-2xl text-lg md:text-xl font-light text-[#404e48] leading-relaxed">
          The Tea List <strong>tea glossary</strong> — 75 essential <strong>tea
          terms</strong> defined plainly across botany, processing, brewing,
          and tasting. Whether you're decoding a packet of{" "}
          <Link to="/glossary/darjeeling" className="underline decoration-dotted underline-offset-4 hover:text-[#1e4a35]">Darjeeling</Link>,
          following a <Link to="/category/brewing-and-the-art-of-tasting" className="underline decoration-dotted underline-offset-4 hover:text-[#1e4a35]">gongfu brewing</Link>{" "}
          guide, or learning to distinguish a{" "}
          <Link to="/glossary/oolong" className="underline decoration-dotted underline-offset-4 hover:text-[#1e4a35]">first-flush oolong</Link>{" "}
          from a winter pluck — this is your reference. Each term links to a
          dedicated definition page and to the longer articles that put the
          word in context.
        </p>
        <p className="mt-4 max-w-2xl text-sm text-[#66726d] font-light italic">
          Curated by Sameera Samarakkody — editor of The Tea List, based in
          Melbourne. Updated as we publish.
        </p>

        {/* Search */}
        <div
          className="mt-12 flex items-center gap-3 max-w-md border-b pb-2"
          style={{ borderColor: "var(--border)" }}
        >
          <Search size={16} className="text-[#66726d]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search terms…"
            className="bg-transparent outline-none text-base flex-1 py-1"
            data-testid="glossary-search"
          />
        </div>

        {/* Category filters */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="glossary-category-filter">
          {["All", ...data.categories].map((c) => {
            const active = c === activeCategory;
            return (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                className={`px-4 py-2 font-mono text-[10px] tracking-widest uppercase transition-colors ${
                  active
                    ? "bg-[#1e4a35] text-white"
                    : "bg-[#f1eadb] text-[#404e48] hover:bg-[#e2d9c6]"
                }`}
                data-testid={`glossary-cat-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {/* A-Z navigation */}
        {lettersWithEntries.length > 1 && (
          <div className="mt-8 flex flex-wrap gap-x-2 gap-y-1" data-testid="glossary-az-nav">
            {ALPHABET.map((l) => {
              const has = grouped[l]?.length;
              return (
                <a
                  key={l}
                  href={has ? `#letter-${l}` : undefined}
                  className={`font-serif text-base ${
                    has
                      ? "text-[#151e1a] hover:text-[#1e4a35] cursor-pointer"
                      : "text-[#cbc7bc] cursor-default"
                  }`}
                >
                  {l}
                </a>
              );
            })}
          </div>
        )}

        {/* Results */}
        <div className="mt-14 space-y-14">
          {loading && (
            <p className="font-mono text-[11px] tracking-widest uppercase text-[#66726d] animate-pulse">
              steeping…
            </p>
          )}
          {!loading && filtered.length === 0 && (
            <p className="text-[#66726d]">No entries match.</p>
          )}
          {!loading &&
            lettersWithEntries.map((letter) => (
              <div key={letter} id={`letter-${letter}`} className="scroll-mt-32">
                <h2 className="font-serif text-3xl md:text-4xl text-[#1e4a35] mb-6">
                  {letter}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                  {grouped[letter].map((e) => (
                    <Link
                      key={e.slug}
                      to={`/glossary/${e.slug}`}
                      className="group block border-l-2 pl-5 py-1 hover:border-[#1e4a35] transition-colors"
                      style={{ borderColor: "var(--border)" }}
                      data-testid={`glossary-entry-${e.slug}`}
                    >
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <h3 className="font-serif text-xl font-medium group-hover:text-[#1e4a35]">
                          {e.term}
                        </h3>
                        {e.pronunciation && (
                          <span className="font-mono text-[10px] tracking-widest text-[#66726d]">
                            /{e.pronunciation}/
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-[#404e48] font-light leading-relaxed text-sm">
                        {e.short_def}
                      </p>
                      <p className="mt-2 font-mono text-[9px] uppercase tracking-widest text-[#1e4a35]">
                        {e.category}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
        </div>

        {/* FAQ — captures question-intent searches and feeds FAQPage JSON-LD. */}
        <section
          className="mt-20 pt-14 border-t"
          style={{ borderColor: "var(--border)" }}
          data-testid="glossary-faq"
        >
          <p className="overline mb-3">Frequently asked</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#151e1a] mb-10">
            Common questions about tea terminology
          </h2>
          <div className="space-y-8 max-w-3xl">
            {FAQ_ITEMS.map((f, i) => (
              <div key={f.q} data-testid={`glossary-faq-${i}`}>
                <h3 className="font-serif text-xl text-[#1e4a35] mb-2">{f.q}</h3>
                <p className="text-[#404e48] font-light leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      </section>

      <Newsletter />
    </div>
  );
}
